import {
	FiMail,
	FiUsers,
	FiBriefcase,
	FiDollarSign,
	FiGlobe,
	FiBarChart2,
	FiLayers,
	FiZap,
	FiShield,
} from 'react-icons/fi';

export const MONEY_BACK_GUARANTEE = {
	title: '30-Day Money-Back Guarantee',
	shortLabel: '30-day money-back guarantee',
	description:
		'Subscribe with confidence. If Ops Pilot is not the right fit within 30 days of your purchase, contact us and we will refund you in full.',
};

export const HERO_ROTATING_WORDS = ['email campaigns', 'SEO on autopilot', 'clients & projects', 'invoicing & billing', 'leads pipeline', 'todos & tasks'];

export const HERO_TRUST_POINTS = [
	// { text: MONEY_BACK_GUARANTEE.shortLabel, highlight: true },
	{ text: 'Secure Stripe checkout' },
	{ text: 'Branded client portals' },
];

export const STACK_MATRIX_FEATURES = [
	{ id: 'email', label: 'Email campaigns' },
	{ id: 'crm', label: 'CRM & leads' },
	{ id: 'projects', label: 'Projects & tasks' },
	{ id: 'invoicing', label: 'Invoicing' },
	{ id: 'ai', label: 'AI & WordPress' },
	{ id: 'portals', label: 'Client portals' },
	{ id: 'analytics', label: 'Analytics' },
	{ id: 'team', label: 'Team access' },
];

export const OPS_PILOT_STACK = {
	id: 'opspilot',
	name: 'Ops Pilot',
	shortName: 'Ops Pilot',
	capabilities: STACK_MATRIX_FEATURES.map((f) => f.id),
};

export const STACK_COMPARISONS = [
	{
		id: 'mailchimp',
		name: 'Mailchimp',
		category: 'Email marketing',
		iconSlug: 'mailchimp',
		iconColor: 'FFE01B',
		brandBg: '#FFE01B',
		capabilities: ['email', 'analytics'],
	},
	{
		id: 'hubspot',
		name: 'HubSpot',
		category: 'CRM & sales',
		iconSlug: 'hubspot',
		iconColor: 'FF7A59',
		brandBg: '#FF7A59',
		capabilities: ['email', 'crm', 'analytics', 'team'],
	},
	{
		id: 'asana',
		name: 'Asana',
		category: 'Project management',
		iconSlug: 'asana',
		iconColor: 'F06A6A',
		brandBg: '#F06A6A',
		capabilities: ['projects', 'team'],
	},
	{
		id: 'monday',
		name: 'Monday',
		category: 'Work OS',
		iconSlug: 'mondaydotcom',
		iconColor: 'FF3D57',
		brandBg: '#6161FF',
		capabilities: ['projects', 'team', 'analytics'],
	},
	{
		id: 'freshbooks',
		name: 'FreshBooks',
		category: 'Invoicing',
		iconSlug: 'freshbooks',
		iconColor: '0075DD',
		brandBg: '#0075DD',
		capabilities: ['invoicing'],
	},
	{
		id: 'quickbooks',
		name: 'QuickBooks',
		category: 'Accounting',
		iconSlug: 'quickbooks',
		iconColor: '2CA01C',
		brandBg: '#2CA01C',
		capabilities: ['invoicing', 'analytics'],
	},
	{
		id: 'activecampaign',
		name: 'ActiveCampaign',
		category: 'Marketing automation',
		iconSlug: 'activecampaign',
		iconColor: '356AE6',
		brandBg: '#356AE6',
		capabilities: ['email', 'analytics'],
	},
	{
		id: 'wordpress',
		name: 'WordPress',
		category: 'SEO & publishing',
		iconSlug: 'wordpress',
		iconColor: '21759B',
		brandBg: '#21759B',
		capabilities: ['ai'],
	},
	{
		id: 'notion',
		name: 'Notion',
		category: 'Docs & wikis',
		iconSlug: 'notion',
		iconColor: '000000',
		brandBg: '#191919',
		capabilities: ['crm', 'team'],
	},
];

/** @deprecated Use STACK_COMPARISONS — kept for simple name lists */
export const REPLACED_TOOLS = STACK_COMPARISONS.map((tool) => tool.name);

export const NAV_LINKS = [
	{ label: 'Platform', href: '#platform' },
	{ label: 'Blogsmith', href: '#blogsmith' },
	{ label: 'Workflow', href: '#workflow' },
	{ label: 'Stories', href: '#testimonials' },
	{ label: 'Pricing', href: '#pricing' },
	{ label: 'FAQ', href: '#faq' },
];

export const HERO_STATS = [
	{ value: 12, suffix: '+', label: 'Hours saved on content / week' },
	{ value: 40, suffix: '%', label: 'Less tool spend' },
	{ value: 3, suffix: 'x', label: 'Faster client delivery' },
	{ value: 9, suffix: '+', label: 'Tools replaced by one cockpit' },
];

export const PLATFORM_PILLARS = [
	{
		id: 'email',
		icon: FiMail,
		label: 'Email Marketing',
		headline: 'Campaigns that scale with your clients',
		description:
			'Build, schedule, and track multi-client campaigns — enterprise delivery without juggling another email tool.',
		bullets: [
			'Contact lists, folders & CSV import',
			'Domain authentication & sender management',
			'Campaign analytics & warm-up controls',
		],
		accent: 'cyan',
		gradient: 'linear(to-br, cyan.400, blue.600)',
	},
	{
		id: 'crm',
		icon: FiUsers,
		label: 'CRM & Clients',
		headline: 'Every lead, client, and task in one place',
		description:
			'Pipelines, todos, and client portfolios in one command center — no more spreadsheet chaos.',
		bullets: [
			'Leads pipeline & task management',
			'Client profiles & relationship history',
			'Role-based access for every teammate',
		],
		accent: 'purple',
		gradient: 'linear(to-br, purple.400, pink.500)',
	},
	{
		id: 'projects',
		icon: FiBriefcase,
		label: 'Projects',
		headline: 'From retainer to deliverable — fully tracked',
		description:
			'Milestones, budgets, and Kanban tasks turn agency work into clear, billable outcomes.',
		bullets: [
			'Tasks, subtasks & file attachments',
			'Budget & hour tracking per project',
			'Tiered portfolio analytics by plan',
		],
		accent: 'orange',
		gradient: 'linear(to-br, orange.400, red.500)',
	},
	{
		id: 'finance',
		icon: FiDollarSign,
		label: 'Invoicing',
		headline: 'Get paid with less friction',
		description:
			'Invoice clients, generate PDFs, and report revenue — without exporting to a separate billing app.',
		bullets: [
			'Invoice PDF generation & client delivery',
			'Bank details & custom numbering',
			'Financial reporting & revenue goals',
		],
		accent: 'green',
		gradient: 'linear(to-br, green.400, teal.600)',
	},
	{
		id: 'ai',
		icon: FiGlobe,
		label: 'WordPress & AI',
		headline: 'Publish SEO content on autopilot',
		description:
			'Blogsmith learns your client\'s voice, generates SEO posts with internal links, and publishes to WordPress on schedule.',
		bullets: [
			'REST-connected WordPress publishing',
			'Recurring AI post automation',
			'Site learning for client-branded output',
		],
		accent: 'blue',
		gradient: 'linear(to-br, blue.400, indigo.600)',
	},
];

export const CAPABILITY_GRID = [
	{
		icon: FiLayers,
		title: 'Branded portals',
		desc: 'White-label subdomains with custom colors, logos, and login experiences.',
	},
	{
		icon: FiBarChart2,
		title: 'Unified analytics',
		desc: 'Campaign, project, and financial insights without duct-taping dashboards.',
	},
	{
		icon: FiZap,
		title: 'Real-time collaboration',
		desc: 'Comments and mentions within each project task keep your team aligned.',
	},
	{
		icon: FiShield,
		title: 'Enterprise-ready gating',
		desc: 'Plan-based features, permissions, and compliance workflows built in.',
	},
];

export const WORKFLOW_STEPS = [
	{
		step: '01',
		title: 'Onboard your workspace',
		desc: 'Spin up a branded portal, invite your team, and connect your sending domain in minutes.',
	},
	{
		step: '02',
		title: 'Centralize clients & campaigns',
		desc: 'Import contacts, organize lists, and launch multi-client campaigns from one command center.',
	},
	{
		step: '03',
		title: 'Execute, publish & invoice',
		desc: 'Run projects, let Blogsmith ship SEO content, and bill clients — all from one cockpit.',
	},
];

export const TESTIMONIALS = [
	{
		name: 'Sarah Jenkins',
		role: 'Founder, Apex Marketing',
		metric: '−40% tool spend',
		avatar:
			'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&q=80',
		text:
			'Ops Pilot replaced our email tool, project tracker, and invoicing stack. We onboard clients faster and our team finally works from one source of truth.',
	},
	{
		name: 'David Chen',
		role: 'Freelance Growth Consultant',
		metric: '+18 active clients',
		avatar:
			'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80',
		text:
			'Blogsmith alone saves me 10+ hours a week on client content. Campaign analytics paid for the subscription in month one.',
	},
	{
		name: 'Elena Rodriguez',
		role: 'Ops Director, Northline Digital',
		metric: '3× faster onboarding',
		avatar:
			'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=150&q=80',
		text:
			'Branded portals made us look enterprise-grade overnight. Clients log into their subdomain; we manage everything behind the scenes.',
	},
];

export const BLOGSMITH_DEMO_SCENES = [
	{
		prompt: 'Write an SEO post about local dental marketing for a Chicago clinic…',
		title: '7 Local SEO Tactics Chicago Dental Clinics Use to Fill Chairs',
		outline: 'Google Business Profile · Local keywords · Patient reviews · Schema markup',
	},
	{
		prompt: 'Create a recurring post about SaaS onboarding best practices…',
		title: 'The 5-Step SaaS Onboarding Flow That Cuts Churn by 23%',
		outline: 'Activation milestones · In-app guides · Email nurture · Success metrics',
	},
	{
		prompt: 'Draft content about eco-friendly packaging for a DTC brand…',
		title: 'How Sustainable Packaging Became This DTC Brand\'s #1 Growth Lever',
		outline: 'Consumer trends · Cost vs. perception · Case study · CTA framework',
	},
];

export const PRICING_TIERS = [
	{
		name: 'Starter',
		monthly: 29,
		yearly: 24,
		tagline: 'Solo operators getting organized',
		highlight: false,
		features: [
			'Core CRM & contacts',
			'Limited campaigns & templates',
			'Essential project tracking',
		],
	},
	{
		name: 'Freelancer',
		monthly: 49,
		yearly: 39,
		tagline: 'Growing independents with multiple clients',
		highlight: false,
		features: [
			'Expanded email sends',
			'Drag-and-drop email builder',
			'Client management & invoicing',
		],
	},
	{
		name: 'Agency',
		monthly: 99,
		yearly: 79,
		tagline: 'Run 10+ clients from one branded portal',
		highlight: true,
		features: [
			'Advanced analytics tiers',
			'Branded workspace portal',
			'Blogsmith AI content & financial reporting',
			'Priority collaboration tools',
		],
	},
	{
		name: 'Bespoke',
		monthly: null,
		yearly: null,
		tagline: 'Custom limits & dedicated support',
		highlight: false,
		features: [
			'Tailored feature limits',
			'Custom onboarding',
			'Strategic account partnership',
		],
	},
];

export const FAQ_ITEMS = [
	{
		id: 'who-for',
		q: 'Who is Ops Pilot CRM for?',
		a: 'Ops Pilot is built for freelancers and agencies who want to run their own business and deliver client work from one place — including marketing campaigns and SEO content. Organize contacts into folders, then attach projects, campaigns, invoices, and websites to each client so everything stays connected.',
		blogsmithReply:
			'Freelancers and agencies — whether you are running your own operation or delivering for clients. Ops Pilot lets you run marketing campaigns and SEO content (that is where I come in), organise contacts into folders, and tie projects, campaigns, invoices, and WordPress sites to each client. One cockpit for your business and theirs.',
	},
	{
		id: 'email-domain',
		q: 'Can I use my own domain for email?',
		a: 'Yes. Domain authentication and sender management are built into the platform so campaigns send from your brand regardless of your subscription plan.',
		blogsmithReply:
			'Absolutely. You can authenticate your own sending domain and manage senders inside Ops Pilot, so every campaign goes out under your brand — on any paid plan.',
	},
	{
		id: 'client-portal',
		q: 'Do clients get their own portal?',
		a: 'All our plans include a branded subdomain (e.g. yourcompany.opspilotcrm.com) with custom login branding.',
		blogsmithReply:
			'Yes — every plan includes a branded client portal on your subdomain (like yourcompany.opspilotcrm.com) with your logo and colours. Clients see your brand; you run everything behind the scenes.',
	},
	{
		id: 'after-signup',
		q: 'What happens after I sign up?',
		a: 'Choose a plan, complete secure Stripe checkout, and unlock features based on your tier. You can upgrade anytime from inside the app.',
		blogsmithReply:
			'Sign up, pick a plan, checkout securely with Stripe, and your workspace unlocks right away based on your tier. You can upgrade limits or features anytime from inside the app — no migration drama.',
	},
	{
		id: 'free-trial',
		q: 'Do you offer a free trial?',
		a: 'We do not offer a free plan or trial. Every subscription is paid upfront — but you are covered by our 30-day money-back guarantee if the platform is not the right fit.',
		blogsmithReply:
			'We do not run a free plan or trial — subscriptions are paid upfront. That said, you are covered by the 30-day money-back guarantee if Ops Pilot is not the right fit. Low risk, full access from day one.',
	},
	{
		id: 'guarantee',
		q: 'How does the 30-day money-back guarantee work?',
		a: 'If you are not satisfied within 30 days of your purchase, contact support@opspilotcrm.com and we will issue a full refund. No lengthy hoops — just reach out within the window.',
		blogsmithReply:
			'Simple: if you are not happy within 30 days of purchase, email support@opspilotcrm.com and we will refund you in full. No hoops, no lengthy forms — just reach out inside the window.',
	},
];

export const TRUST_LOGOS = [
	'Apex Digital',
	'Northline',
	'Pulse & Co',
	'Meridian Ops',
	'Studio K',
];
